import { ClipboardList, Eye, FileText, MessageSquare } from "lucide-react";
import type { Alert } from "@/features/alert/types";
import { InlineActionButton } from "@/components/ui/InlineActionButton";
import { ChangeRequestAlertActions } from "./ChangeRequestAlertActions";
import { DefaultAlertActions } from "./DefaultAlertActions";
import { ProductRequestAlertActions } from "./ProductRequestAlertActions";
import { ReturnDecisionActions } from "./ReturnDecisionActions";
import type { AlertActionMode } from "./alertCardRules";

type AlertActionsProps = {
  alert: Alert;
  alertActionMode: AlertActionMode;
  isReturnAlert: boolean;
  isBusy: boolean;
  canEditNote: boolean;
  canMarkSeen: boolean;
  canManageChangeRequest: boolean;
  canReviewProduct: boolean;
  canOpenSupplierPriceList: boolean;
  canOpenReportWorkspace: boolean;
  canReplaceReturn: boolean;
  canCancelReturn: boolean;
  onOpenTargetOrder: () => void;
  onToggleSubstitution: () => void;
  onCancelReturn: () => void;
  onApproveChangeRequest: () => void;
  onToggleReject: () => void;
  onMarkSeen: () => void;
  onOpenProductReview: () => void;
  onOpenSupplierPriceList: () => void;
  onOpenReportWorkspace: () => void;
  onToggleNote: () => void;
};

export function AlertActions({
  alert,
  alertActionMode,
  isReturnAlert,
  isBusy,
  canEditNote,
  canMarkSeen,
  canManageChangeRequest,
  canReviewProduct,
  canOpenSupplierPriceList,
  canOpenReportWorkspace,
  canReplaceReturn,
  canCancelReturn,
  onOpenTargetOrder,
  onToggleSubstitution,
  onCancelReturn,
  onApproveChangeRequest,
  onToggleReject,
  onMarkSeen,
  onOpenProductReview,
  onOpenSupplierPriceList,
  onOpenReportWorkspace,
  onToggleNote,
}: AlertActionsProps) {
  const isSuperProductRequestAlert =
    alertActionMode === "super-product-request";
  const isSupplierPriceListDueAlert =
    alertActionMode === "supplier-price-list-due";

  return (
    <>
      {isReturnAlert ? (
        <ReturnDecisionActions
          isBusy={isBusy}
          canReplace={canReplaceReturn}
          canCancel={canCancelReturn}
          onOpenTargetOrder={onOpenTargetOrder}
          onToggleSubstitution={onToggleSubstitution}
          onCancelReturn={onCancelReturn}
        />
      ) : canManageChangeRequest ? (
        <ChangeRequestAlertActions
          isBusy={isBusy}
          onOpenTargetOrder={onOpenTargetOrder}
          onApproveChangeRequest={onApproveChangeRequest}
          onToggleReject={onToggleReject}
        />
      ) : isSuperProductRequestAlert ? (
        <ProductRequestAlertActions
          canMarkSeen={canMarkSeen}
          isBusy={isBusy}
          canResolve={canReviewProduct}
          onMarkSeen={onMarkSeen}
          onOpenProductReview={onOpenProductReview}
        />
      ) : isSupplierPriceListDueAlert && canOpenSupplierPriceList ? (
        <>
          <InlineActionButton
            tone="accent"
            onClick={onOpenSupplierPriceList}
            disabled={isBusy}
            icon={<ClipboardList size={12} />}
          >
            Criar lista
          </InlineActionButton>
          {canMarkSeen ? (
            <InlineActionButton
              onClick={onMarkSeen}
              disabled={isBusy}
              icon={<Eye size={12} />}
            >
              Marcar visto
            </InlineActionButton>
          ) : null}
        </>
      ) : canOpenReportWorkspace ? (
        <InlineActionButton
          tone="accent"
          onClick={onOpenReportWorkspace}
          disabled={isBusy}
          icon={<FileText size={12} />}
        >
          Abrir relatórios
        </InlineActionButton>
      ) : alert.alert_type === "report_pedido_due" ? (
        <>
          <InlineActionButton
            tone="accent"
            onClick={onOpenTargetOrder}
            disabled={isBusy}
            icon={<ClipboardList size={12} />}
          >
            Abrir relatórios
          </InlineActionButton>
          {canMarkSeen ? (
            <InlineActionButton
              onClick={onMarkSeen}
              disabled={isBusy}
              icon={<Eye size={12} />}
            >
              Marcar visto
            </InlineActionButton>
          ) : null}
        </>
      ) : (
        <DefaultAlertActions
          canMarkSeen={canMarkSeen}
          isBusy={isBusy}
          onMarkSeen={onMarkSeen}
        />
      )}
      {!isReturnAlert && canEditNote && (
        <InlineActionButton
          onClick={onToggleNote}
          disabled={isBusy}
          icon={<MessageSquare size={12} />}
        >
          Nota
        </InlineActionButton>
      )}
    </>
  );
}
